import { Fail, FailConstructorI } from './Fail';

/* ---------------------------------- Types --------------------------------- */

/**
 * SimpleResult
 *
 * A result whose failure doesn't connote an error
 */
export type SimpleResult<
    SuccessPayload,
    FailurePayload extends unknown | undefined = undefined,
> =
    | {
          ok: true;
          payload: SuccessPayload;
      }
    | (FailurePayload extends undefined
          ? { ok: false; payload?: never }
          : { ok: false; payload: FailurePayload });

/**
 * Result
 *
 * Failure of this result type constitutes a error
 */
export type Result<Data, F extends Fail = Fail> = SimpleResult<Data, F>;

/* --------------------------------- Helpers -------------------------------- */

/**
 * ok
 *
 * Return successful result
 *
 * @param data - Data payload to pass with the result
 * @returns
 */
export function ok<Data>(data: Data): Readonly<SimpleResult<Data, never>> {
    return Object.freeze({
        ok: true,
        payload: data,
    });
}

/**
 * fail
 *
 * Returns a simple result fail
 *
 * Useful when implementing results that don't necessarily fail
 * e.g. Dismissing a modal
 *
 * @param payload - Optional payload parameter
 * @returns
 */
export function fail<FailurePayload extends unknown | undefined = undefined>(
    payload?: FailurePayload,
): SimpleResult<never, FailurePayload> {
    return Object.freeze({
        ok: false,
        payload,
    }) as SimpleResult<never, FailurePayload>;
}

/**
 * err
 *
 * Creates an error result from an instance of a Fail class
 *
 * @param f - An existing instance of a Fail class or object of FailConstructor props
 */
export function err<F extends Fail = Fail>(f: F): Result<never, F>;
/**
 * err
 *
 * Creates an error result from an instance of a Fail class
 *
 * @param f - An object containing FailConstructor props
 */
export function err(f: FailConstructorI): Result<never, Fail>;

export function err<F extends Fail = Fail>(
    f: F | FailConstructorI,
): Result<never, Fail> {
    const failure = f instanceof Fail ? f : new Fail(f);

    return Object.freeze({
        ok: false,
        payload: failure,
    });
}
